import type { GateExtractor } from "../../chat/chat-tool-config";

/** Defensively parse an unknown `milestones` payload into `FiredMilestone[]`. */
function parseMilestones(raw: unknown): Array<{ id: string; label: string; description?: string }> {
  if (!Array.isArray(raw)) return [];
  const out: Array<{ id: string; label: string; description?: string }> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== "object") continue;
    const e = entry as Record<string, unknown>;
    if (typeof e.id !== "string" || typeof e.label !== "string") continue;
    out.push({
      id: e.id,
      label: e.label,
      ...(typeof e.description === "string" && e.description ? { description: e.description } : {}),
    });
  }
  return out;
}

/**
 * `save_intake_fields` gate extractor — projects the
 * `output.transition.{title, description, milestones}` payload into a
 * `ChatTransitionItem` that closes the current step and gates the
 * next step (stage transition card pattern).
 *
 * Returns `null` until the tool has an output (transitions are only
 * known post-execution) or when the output has no transition payload.
 *
 * When `milestones[]` is present, the card renders its milestone variant
 * using the last entry; the full array is preserved for telemetry/debug.
 *
 * Registered in `chat/chat-tool-config.ts` under the `save_intake_fields`
 * entry's `gateExtractor` field. Co-located with other `save_intake_fields`
 * projections so the tool-output schema stays the single source of truth
 * across render + projection.
 */
export const extractSaveIntakeTransition: GateExtractor = (messageId, partIdx, toolPart) => {
  if (toolPart.state !== "output-available" || !toolPart.output) return null;
  const output = toolPart.output as Record<string, unknown>;
  const transition = output.transition as
    | { title?: unknown; description?: unknown; milestones?: unknown }
    | undefined;
  if (!transition || typeof transition.title !== "string" || !transition.title) {
    return null;
  }
  const description =
    typeof transition.description === "string" ? transition.description : undefined;
  const milestones = parseMilestones(transition.milestones);

  return {
    kind: "transition",
    id: `${messageId}-transition-${partIdx}`,
    title: transition.title,
    description,
    ...(milestones.length > 0 && { milestones }),
  };
};
